import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { adminApi, apiError } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import { fmtDate, StatusPill, Panel, PageHeader, Th, Td, Empty } from "@/components/admin/parts";
import { ConfirmDialog } from "@/components/admin/ConfirmDialog";
import FinancePanel from "@/pages/admin/FinancePanel";

const TABS = [
  { key: "general", label: "Ogólne" },
  { key: "email", label: "E-mail" },
  { key: "security", label: "Bezpieczeństwo" },
  { key: "finance", label: "Finanse" },
  { key: "integrations", label: "Integracje" },
  { key: "audit", label: "Audyt" },
];

const FLAG_LABELS = {
  welcomeCredits: "Bonus powitalny (10 kredytów)",
  aiText: "Operacje AI — tekst",
  aiImage: "Operacje AI — obraz",
  fileAnalysis: "Analiza plików",
  autoTopUp: "Automatyczne doładowanie",
  billingDocuments: "Dokumenty rozliczeniowe",
};

const Toggle = ({ on, onClick, testId }) => (
  <button data-testid={testId} onClick={onClick} role="switch" aria-checked={on} className={`relative h-6 w-11 rounded-full transition-colors ${on ? "bg-brand-accent" : "bg-brand-elevated"}`}>
    <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${on ? "left-[22px]" : "left-0.5"}`} />
  </button>
);

const Field = ({ label, value }) => (
  <div className="rounded-card border border-brand-line bg-brand-bg/50 px-3 py-2.5">
    <p className="text-[11px] uppercase tracking-wide text-brand-muted">{label}</p>
    <p className="mt-0.5 text-sm text-brand-text break-all">{value || "—"}</p>
  </div>
);

export default function Settings() {
  const [tab, setTab] = useState("general");
  const [data, setData] = useState(null);
  const [flags, setFlags] = useState({});
  const [pendingFlag, setPendingFlag] = useState(null);
  const [events, setEvents] = useState(null);
  const [audit, setAudit] = useState(null);
  const [testEmail, setTestEmail] = useState("");
  const [testTpl, setTestTpl] = useState("verification");

  const load = () => adminApi.settings().then((d) => { setData(d); setFlags(d.featureFlags || {}); }).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (tab === "email") adminApi.emailEvents({ limit: 50 }).then((d) => setEvents(d.items)).catch(() => {});
    if (tab === "audit") adminApi.audit({ limit: 100 }).then((d) => setAudit(d.items)).catch(() => {});
  }, [tab]);

  const confirmFlag = async () => {
    const key = pendingFlag;
    const next = { ...flags, [key]: !flags[key] };
    setFlags(next); setPendingFlag(null);
    try { await adminApi.updateFlags(next); toast.success("Zapisano ustawienia."); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); load(); }
  };

  const sendTest = async () => {
    if (!testEmail.trim()) { toast.error("Podaj adres odbiorcy."); return; }
    try {
      const res = await adminApi.testEmail({ recipient: testEmail.trim(), template: testTpl });
      toast.success(res.mock ? "Tryb MOCK — zapisano zdarzenie testowe (brak realnej wysyłki)." : "Wysłano wiadomość testową.");
      adminApi.emailEvents({ limit: 50 }).then((d) => setEvents(d.items));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const delEvent = async (id) => {
    try { await adminApi.deleteEmailEvent(id); setEvents((ev) => ev.filter((e) => e.id !== id)); toast.success("Usunięto zdarzenie."); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (!data) return <div className="text-brand-muted">Ładowanie…</div>;

  return (
    <div>
      <PageHeader title="Ustawienia" subtitle="Konfiguracja, integracje, bezpieczeństwo i audyt" />
      <div className="mb-5 flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button key={t.key} data-testid={`${ADMIN.settingsTab}-${t.key}`} onClick={() => setTab(t.key)} className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${tab === t.key ? "bg-brand-accent text-brand-on" : "border border-brand-line text-brand-sec hover:text-brand-text"}`}>{t.label}</button>
        ))}
      </div>

      {tab === "general" && (
        <Panel title="Flagi funkcji" testId={ADMIN.settingsFlags}>
          <div className="space-y-1">
            {Object.keys(FLAG_LABELS).map((key) => (
              <div key={key} className="flex items-center justify-between border-b border-brand-line/60 py-3 last:border-0">
                <span className="text-sm text-brand-text">{FLAG_LABELS[key]}</span>
                <Toggle testId={`${ADMIN.flagToggle}-${key}`} on={!!flags[key]} onClick={() => setPendingFlag(key)} />
              </div>
            ))}
          </div>
        </Panel>
      )}

      {tab === "email" && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Panel title="Konfiguracja Resend">
            <div className="space-y-2">
              <Field label="Nadawca (EMAIL_FROM)" value={data.integrations.resend.sender} />
              <Field label="Reply-To" value={data.integrations.resend.replyTo} />
              <Field label="Adres wsparcia" value={data.integrations.resend.supportEmail} />
              <Field label="FRONTEND_URL" value={data.integrations.resend.frontendUrl} />
              <div className="flex items-center gap-2 pt-1">
                <span className="text-xs text-brand-muted">Klucz API:</span>
                <StatusPill status={data.integrations.resend.configured ? "settled" : "ZAMKNIETA"} label={data.integrations.resend.configured ? "SKONFIGUROWANY" : "BRAK (tryb MOCK)"} />
                <span className="text-xs text-brand-muted">••••••••  (ukryty, nigdy nie zwracany przez API)</span>
              </div>
            </div>
            <div className="mt-5 rounded-card border border-brand-line bg-brand-bg/50 p-4">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-brand-muted">Wyślij wiadomość testową</p>
              <div className="flex flex-wrap gap-2">
                <input data-testid={ADMIN.testEmailInput} value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="odbiorca@example.com" className="h-10 flex-1 rounded-field border border-brand-line bg-brand-surface px-3 text-sm text-brand-text outline-none" />
                <select value={testTpl} onChange={(e) => setTestTpl(e.target.value)} className="h-10 rounded-field border border-brand-line bg-brand-surface px-2 text-sm text-brand-text outline-none">
                  <option value="verification">Weryfikacja</option>
                  <option value="reset">Reset hasła</option>
                </select>
                <button data-testid={ADMIN.testEmailSubmit} onClick={sendTest} className="h-10 rounded-field bg-brand-accent px-4 text-sm font-semibold text-brand-on">Wyślij test</button>
              </div>
            </div>
            <div className="mt-4">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-brand-muted">Szablony</p>
              <div className="space-y-1">
                {data.templates.map((t) => (
                  <div key={t.key} className="flex items-center justify-between border-b border-brand-line/60 py-2 last:border-0">
                    <div><span className="text-sm text-brand-text">{t.name}</span><span className="ml-2 text-xs text-brand-muted">{t.desc}</span></div>
                    <StatusPill status="settled" label={t.status} />
                  </div>
                ))}
              </div>
            </div>
          </Panel>
          <Panel title="Historia zdarzeń e-mail" testId={ADMIN.emailEventsTable}>
            {!events ? <Empty>Ładowanie…</Empty> : events.length === 0 ? <Empty>Brak zdarzeń.</Empty> : (
              <div className="max-h-[520px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-brand-line"><Th>Data</Th><Th>Typ</Th><Th>Odbiorca</Th><Th>Status</Th><Th></Th></tr></thead>
                  <tbody>
                    {events.map((e) => (
                      <tr key={e.id} className="border-b border-brand-line/60">
                        <Td className="whitespace-nowrap text-brand-sec">{fmtDate(e.created_at)}</Td>
                        <Td>{e.kind}</Td>
                        <Td className="text-brand-muted">{e.recipient_masked}</Td>
                        <Td><StatusPill status={e.status === "DELIVERED" ? "settled" : e.status === "FAILED" ? "failed" : "W_TOKU"} label={e.status} /></Td>
                        <Td><button data-testid="admin-email-event-delete" onClick={() => delEvent(e.id)} className="rounded-field border border-brand-line p-1.5 text-red-300 hover:border-red-400/50"><Trash2 size={13} /></button></Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="mt-3 text-xs text-brand-muted">Odbiorcy zamaskowani. Treść wiadomości i tokeny nie są przechowywane ani logowane.</p>
              </div>
            )}
          </Panel>
        </div>
      )}

      {tab === "security" && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Panel title="Tokeny i sesje">
            <div className="space-y-2">
              <Field label="Ważność linku weryfikacyjnego" value={`${data.tokens.verificationTtlHours} godz.`} />
              <Field label="Ważność linku resetu hasła" value={`${data.tokens.resetTtlHours} godz.`} />
              <div className="rounded-card border border-brand-line bg-brand-bg/50 px-3 py-2.5">
                <p className="text-[11px] uppercase tracking-wide text-brand-muted">Reguły tokenów</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {data.tokens.security.map((s) => <StatusPill key={s} status="settled" label={s} />)}
                </div>
              </div>
            </div>
          </Panel>
          <Panel title="Prywatność i logi">
            <div className="flex items-center justify-between border-b border-brand-line/60 py-3">
              <span className="text-sm text-brand-text">Treść wiadomości nie jest logowana</span>
              <StatusPill status={data.tokens.contentLogged ? "failed" : "settled"} label={data.tokens.contentLogged ? "LOGOWANA" : "NIE LOGOWANA"} />
            </div>
            <div className="flex items-center justify-between border-b border-brand-line/60 py-3">
              <span className="text-sm text-brand-text">Surowe tokeny w bazie</span>
              <StatusPill status="settled" label="TYLKO HASH SHA-256" />
            </div>
            <p className="mt-3 text-xs text-brand-muted">Klucze i tokeny nigdy nie występują w odpowiedziach API ani w logach.</p>
          </Panel>
        </div>
      )}

      {tab === "finance" && <FinancePanel />}

      {tab === "integrations" && (
        <Panel title="Status integracji">
          <div className="space-y-1">
            <Row label="Resend (e-mail transakcyjny)" ok={data.integrations.resend.configured} extra={data.integrations.resend.configured ? "aktywny" : "brak klucza (MOCK)"} />
            <Row label="Stripe (płatności)" ok extra={`tryb: ${data.integrations.stripe.mode}`} />
            <Row label="Emergent LLM (AI)" ok={data.integrations.emergentLlm.configured} />
          </div>
          <div className="mt-4 rounded-card border border-brand-line bg-brand-bg/50 p-3 text-xs text-brand-muted">
            Kurs USD→PLN (szacowany koszt AI): {data.usdPlnRate}. Zmiana integracji jest zapisywana w audycie.
          </div>
        </Panel>
      )}

      {tab === "audit" && (
        <Panel title="Dziennik audytu" testId={ADMIN.auditTable}>
          {!audit ? <Empty>Ładowanie…</Empty> : audit.length === 0 ? <Empty>Brak wpisów audytu.</Empty> : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px]">
                <thead><tr className="border-b border-brand-line"><Th>Data</Th><Th>Administrator</Th><Th>Akcja</Th><Th>Obiekt</Th><Th>Powód</Th><Th>IP</Th></tr></thead>
                <tbody>
                  {audit.map((a) => (
                    <tr key={a.id} className="border-b border-brand-line/60">
                      <Td className="whitespace-nowrap text-brand-sec">{fmtDate(a.created_at)}</Td>
                      <Td className="text-brand-sec">{a.admin_email}</Td>
                      <Td><StatusPill status="KOREKTA" label={a.action} /></Td>
                      <Td className="text-brand-muted">{a.entity}: {a.entity_id}</Td>
                      <Td className="max-w-[200px] truncate text-brand-sec">{a.reason || "—"}</Td>
                      <Td className="text-brand-muted">{a.ip || "—"}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      )}

      <ConfirmDialog
        open={!!pendingFlag}
        onOpenChange={(o) => !o && setPendingFlag(null)}
        title="Zmiana flagi funkcji"
        description={pendingFlag ? `Przełączysz: ${FLAG_LABELS[pendingFlag]}.` : ""}
        confirmLabel="Zapisz"
        onConfirm={confirmFlag}
        testId="admin-flag-confirm"
      />
    </div>
  );
}

const Row = ({ label, ok, extra }) => (
  <div className="flex items-center justify-between border-b border-brand-line/60 py-3 last:border-0">
    <span className="text-sm text-brand-text">{label}</span>
    <span className="flex items-center gap-2 text-sm">
      {extra && <span className="text-brand-muted">{extra}</span>}
      <StatusPill status={ok ? "settled" : "ZAMKNIETA"} label={ok ? "Aktywne" : "Nieaktywne"} />
    </span>
  </div>
);
